import {
  Card,
  Stack,
  Title,
  Text,
  Button,
  Badge,
  Box,
  Group,
  Paper,
  SimpleGrid,
} from '@mantine/core';
import GameScreen from '../components/GameScreen';

function GameFinishedScreen({
  gameName,
  roomCode,
  playerName,
  playerScore,
  finalResults,
  playerId,
  onJoinNewGame,
  isGameController,
  onStartNewGame,
}) {
  const isWinner = finalResults?.winners.find(w => w.id === playerId);

  return (
    <GameScreen
      gameName={gameName}
      roomCode={roomCode}
      onQuit={onJoinNewGame}
      background="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      scroll
    >
      <Stack gap="lg">

            {/* Winner Announcement */}
            {finalResults && finalResults.winners.length > 0 && (
              <Card
                radius="lg"
              >
                <Stack gap="md" align="center">
                  <Title order={1} size="xl">
                    {finalResults.winners.length > 1 ? '🤝' : '🏆'}
                    {finalResults.winners.length > 1 ? ' TIE GAME!' : ' WINNER!'}
                  </Title>
                  <Text size="lg" fw={600}>
                    {finalResults.winners.map(w => w.name).join(', ')}
                  </Text>
                  {isWinner && (
                    <Box>
                      <Text size="lg" c="yellow" fw={600}>
                        🎊 Congratulations! You won! 🎊
                      </Text>
                    </Box>
                  )}
                </Stack>
              </Card>
            )}

            {/* Final Leaderboard */}
            <Card
              p="lg"
              radius="lg"
            >
              <Stack gap="md">
                <Box ta="center">
                  <Title order={2} size="lg">
                    Final Leaderboard
                  </Title>
                </Box>

                <SimpleGrid cols={1} spacing="sm">
                  {finalResults?.finalScores.map((player, index) => {
                    const isCurrentPlayer = player.id === playerId;
                    let emoji = '';

                    if (index === 0) emoji = '🏆';
                    else if (index === 1) emoji = '🥈';
                    else if (index === 2) emoji = '🥉';
                    else emoji = '🎭';

                    return (
                      <Paper
                        key={player.id}

                        radius="md"
                      >
                        <Group>
                          <Group>
                            <Text size="xl">{emoji}</Text>
                            <Box>
                              <Text fw={600} c={isCurrentPlayer ? 'green' : 'black'}>
                                {player.name}
                                {isCurrentPlayer && ' (You)'}
                              </Text>
                              <Text size="sm" c="dimmed">
                                Rank #{index + 1}
                              </Text>
                            </Box>
                          </Group>
                          <Badge color="yellow" variant="filled" size="lg">
                            {player.score} pts
                          </Badge>
                        </Group>
                      </Paper>
                    );
                  })}
                </SimpleGrid>
              </Stack>
            </Card>

            {/* Game Drawings Gallery */}
            {finalResults?.roundHistory && finalResults.roundHistory.length > 0 && (
              <Card
                p="lg"
                radius="lg"
              >
                <Stack gap="md">
                  <Box ta="center">
                    <Title order={2} size="lg">
                      🎨 Game Gallery
                    </Title>
                    <Text size="sm" c="dimmed">
                      All drawings and awards from each round
                    </Text>
                  </Box>

                  <Stack gap="lg">
                    {finalResults.roundHistory.map((round) => (
                      <Paper
                        key={round.round}
                        p="md"
                        radius="md"
                        withBorder
                      >
                        <Stack gap="md">
                          {/* Round Header */}
                          <Box ta="center">
                            <Text fw={600} size="md">
                              Round {round.round}: {round.prompt}
                            </Text>
                            <Text size="sm" c="dimmed">
                              Judge: {round.judgeName}
                            </Text>
                          </Box>

                          {/* Judge's Award */}
                          {round.judgeAward && (
                            <Box ta="center">
                              <Text size="sm" fw={500} mb={4}>
                                🏆 {round.judgeName}'s Award for {round.winner?.playerName}:
                              </Text>
                              <Box
                                style={{
                                  border: '2px solid #ffd43b',
                                  borderRadius: '8px',
                                  padding: '8px',
                                  backgroundColor: '#fff9db',
                                  display: 'inline-block'
                                }}
                              >
                                <img 
                                  src={round.judgeAward} 
                                  alt={`Award for ${round.winner?.playerName}`}
                                  style={{
                                    maxWidth: '60%',
                                    maxHeight: '20vh',
                                    borderRadius: '4px'
                                  }}
                                />
                              </Box>
                            </Box>
                          )}

                          {/* All Drawings */}
                          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                            {round.drawings.map((drawing) => (
                              <Paper
                                key={drawing.playerId}
                                p="sm"
                                radius="sm"
                                withBorder
                                style={{
                                  backgroundColor: drawing.rank === 1 ? '#f0f9ff' : 'white',
                                  border: drawing.rank === 1 ? '2px solid #0ea5e9' : '1px solid #ddd'
                                }}
                              >
                                <Stack gap="xs" align="center">
                                  <Group>
                                    <Text size="sm" fw={600}>
                                      {drawing.playerName}
                                      {drawing.playerId === playerId && ' (You)'}
                                    </Text>
                                    {drawing.rank === 1 && (
                                      <Badge color="blue" size="sm">Winner</Badge>
                                    )}
                                  </Group>
                                  <img 
                                    src={drawing.drawing} 
                                    alt={`${drawing.playerName}'s drawing`}
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: '15vh',
                                      borderRadius: '4px',
                                      border: '1px solid #ddd'
                                    }}
                                  />
                                </Stack>
                              </Paper>
                            ))}
                          </SimpleGrid>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Stack>
              </Card>
            )}

            {/* Thank You & New Game */}
            <Card
              shadow="lg"
              p="lg"
              radius="lg"
              ta="center"
            >
              <Stack gap="md">
                <Text size="lg" fw={600}>
                  Thanks for playing Doodle Poodle!
                </Text>
                {isGameController && (
                  <Button
                    size="lg"
                    fullWidth
                    onClick={onStartNewGame}
                    variant="gradient"
                    gradient={{ from: 'purple', to: 'pink' }}
                    sx={{ fontSize: '1.2rem' }}
                  >
                    🔄 Start New Game
                  </Button>
                )}

                <Button
                  size="lg"
                  fullWidth
                  onClick={onJoinNewGame}
                  variant="gradient"
                  gradient={{ from: 'green', to: 'blue' }}
                  sx={{ fontSize: '1.2rem' }}
                >
                  🚀 Join Different Game
                </Button>
              </Stack>
            </Card>
      </Stack>
    </GameScreen>
  );
}

export default GameFinishedScreen;